import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiRequest } from "../utils/api";
import "../styles/SearchForm.css";
import FilterModal from "./FilterModal";
import InventoryTable from "./InventoryTable";
import Pagination from "./Pagination";
import PriceLabels from "./PriceLabels";
import useInventoryViewStore from "../stores/useInventoryViewStore";

const SearchForm = ({
  query = "",
  storeId,
  registerName,
  requestShowPriceLabel,
  soldWithinYear,
  onToggleSoldWithinYear,
  categoryId,
  pageResetKey,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pageSize = 30;

  const location = useLocation();
  const inventoryTableRef = useRef(null);
  const navigate = useNavigate();

  const actualShowPriceLabel = useInventoryViewStore((s) => s.actualShowPriceLabel);
  const setActualShowPriceLabel = useInventoryViewStore((s) => s.setActualShowPriceLabel);
  const clearRequestShowPriceLabel = useInventoryViewStore((s) => s.clearRequestShowPriceLabel);

  // 手動検索/URL変更用 controller
  const requestControllerRef = useRef(null);
  // 自動更新用 controller
  const pollingControllerRef = useRef(null);
  // 自動更新タイマー
  const pollingTimerRef = useRef(null);
  // 自動更新中フラグ
  const isPollingFetchRunningRef = useRef(false);

  useEffect(() => {
    return () => {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
        requestControllerRef.current = null;
      }
      if (pollingControllerRef.current) {
        pollingControllerRef.current.abort();
        pollingControllerRef.current = null;
      }
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      isPollingFetchRunningRef.current = false;
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  const abortPollingRequest = useCallback(() => {
    if (pollingControllerRef.current) {
      pollingControllerRef.current.abort();
      pollingControllerRef.current = null;
    }
    isPollingFetchRunningRef.current = false;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const restoredFilters = {};

    params.forEach((value, key) => {
      if (key === "q" || key === "page") return;

      if (value === "true") {
        restoredFilters[key] = true;
      } else if (value === "false") {
        restoredFilters[key] = false;
      } else if (!isNaN(value)) {
        restoredFilters[key] = Number(value);
      } else {
        restoredFilters[key] = value;
      }
    });

    const q = params.get("q") || "";
    const page = Number(params.get("page")) || 1;

    setSearchQuery(q);
    setCurrentPage(page);
    setFilters(restoredFilters);

    // URL変更時は手動検索扱い
    fetchItems(page, q, restoredFilters, { isPolling: false });
  }, [location.search]);

  // セッション状態クリア処理
  useEffect(() => {
    return () => {
      const fromPath = location.pathname;
      const toPath = window.location.pathname;

      const leavingInventoryList = fromPath === "/inventory_list";
      const goingToInventoryDetail = toPath.startsWith("/inventory_detail/");

      if (leavingInventoryList && !goingToInventoryDetail) {
        console.log("🧹 inventory_list から別ページへ → セッション状態をリセット");

        const keysToClear = [
          "inventory_container_scroll",
          "showPriceLabel",
          "requestShowPriceLabel",
          "actualShowPriceLabel",
        ];

        keysToClear.forEach((key) => sessionStorage.removeItem(key));
      } else {
        console.log("📌 ラベル表示 or 詳細表示 → セッション保持");
      }
    };
  }, [location.pathname]);

  // カテゴリー切り替え時ページ1へ
  useEffect(() => {
    if (!pageResetKey) return;

    setCurrentPage(1);

    fetchItems(
      1,
      searchQuery,
      {
        ...filters,
        ...(soldWithinYear && { category_id: categoryId }),
      },
      { isPolling: false }
    );

    inventoryTableRef.current?.scrollToTop();
  }, [pageResetKey]);

  // requestShowPriceLabel による切り替え
  useEffect(() => {
    if (requestShowPriceLabel !== null) {
      setActualShowPriceLabel(requestShowPriceLabel);
      clearRequestShowPriceLabel();
    }
  }, [requestShowPriceLabel, setActualShowPriceLabel, clearRequestShowPriceLabel]);

  const fetchItems = useCallback(
    async (page, keyword, appliedFilters = {}, options = { isPolling: false }) => {
      const { isPolling = false } = options;

      // 手動検索時だけ前回の手動リクエストを止める
      if (!isPolling && requestControllerRef.current) {
        requestControllerRef.current.abort();
      }

      // polling時だけ前回の polling リクエストを止める
      // ただし通常は isPollingFetchRunningRef で重複防止するので、
      // ここは「残骸」がある時の保険
      if (isPolling && pollingControllerRef.current) {
        pollingControllerRef.current.abort();
      }

      const controller = new AbortController();

      if (isPolling) {
        pollingControllerRef.current = controller;
      } else {
        requestControllerRef.current = controller;
      }

      try {
        const parts = keyword.split(",");
        const baseParams = parts.length >= 5 ? { id: parts[0] } : { q: keyword };

        const params = {
          ...baseParams,
          ...appliedFilters,
          ...(soldWithinYear && { sold_within_year: "true" }),
          ...(soldWithinYear && categoryId && { category_id: categoryId }),
          page,
          page_size: pageSize,
        };

        const queryString = new URLSearchParams(params).toString();

        const data = await apiRequest(
          `inventory/inventory/?${queryString}`,
          "GET",
          null,
          {},
          true,
          controller.signal
        );

        setItems(data.results || []);
        setTotalItems(data.count || 0);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("在庫データ取得エラー:", error);
      } finally {
        if (isPolling) {
          if (pollingControllerRef.current === controller) {
            pollingControllerRef.current = null;
          }
        } else {
          if (requestControllerRef.current === controller) {
            requestControllerRef.current = null;
          }
        }
      }
    },
    [soldWithinYear, categoryId]
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    const params = new URLSearchParams(location.search);

    params.set("q", searchQuery);
    params.set("page", 1);

    setActualShowPriceLabel(false);

    navigate(`/inventory_list?${params.toString()}`);
  };

  const normalizeFiltersForUrl = (filters) => {
    const result = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (value === "") return;

      if (typeof value === "boolean") {
        result[key] = value ? "true" : "false";
      } else {
        result[key] = String(value);
      }
    });
    return result;
  };

  const applyFilters = (newFilters) => {
    const normalized = normalizeFiltersForUrl(newFilters);

    navigate(
      `/inventory_list?${new URLSearchParams({
        ...normalized,
        page: 1,
      }).toString()}`
    );
  };

  const updateItem = (id, updatedFields) => {
    setItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, ...updatedFields } : item))
    );
  };

  // 自動リフレッシュ
  // 空検索でも「現在表示中の一覧」を最新化したいので polling 自体は許可
  // ただし表示中アイテムが無い時は実行しない
  useEffect(() => {
    stopPolling();

    if (actualShowPriceLabel) return;
    if (items.length === 0) return;

    let cancelled = false;

    const loop = async () => {
      if (cancelled) return;

      if (isPollingFetchRunningRef.current) {
        console.log("[SearchForm] skip polling (still fetching)");
      } else {
        isPollingFetchRunningRef.current = true;
        try {
          await fetchItems(currentPage, searchQuery, filters, { isPolling: true });
        } finally {
          isPollingFetchRunningRef.current = false;
        }
      }

      if (cancelled) return;
      pollingTimerRef.current = setTimeout(loop, 10000);
    };

    pollingTimerRef.current = setTimeout(loop, 10000);

    return () => {
      cancelled = true;
      stopPolling();
      abortPollingRequest();
    };
  }, [
    actualShowPriceLabel,
    items.length,
    currentPage,
    searchQuery,
    filters,
    fetchItems,
    stopPolling,
    abortPollingRequest,
  ]);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="search-container-center">
          <div className="search-container">
            <div className="search-inputcontainer">
              <button
                type="button"
                className="filter-btn"
                onClick={() => setIsModalOpen(true)}
              >
                +
              </button>
              <input
                type="text"
                name="q"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="検索キーワード"
                className="search-box"
              />
              <button
                type="button"
                className="clear-btn"
                onClick={() => {
                  setActualShowPriceLabel(false);
                  navigate("/inventory_list");
                }}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      </form>

      {actualShowPriceLabel ? (
        <PriceLabels
          toggleInventory={() => setActualShowPriceLabel(false)}
          storeId={storeId}
          registerName={registerName}
        />
      ) : (
        <>
          <InventoryTable
            ref={inventoryTableRef}
            items={items}
            updateItem={updateItem}
            storeId={storeId}
            fetchItems={() => fetchItems(currentPage, searchQuery, filters, { isPolling: false })}
            togglePriceLabel={() => setActualShowPriceLabel(true)}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / pageSize)}
            onPageChange={(page) => {
              navigate(
                `/inventory_list?${new URLSearchParams({
                  ...Object.fromEntries(new URLSearchParams(location.search)),
                  page,
                }).toString()}`
              );
              inventoryTableRef.current?.scrollToTop();
            }}
          />
        </>
      )}

      {isModalOpen && (
        <FilterModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          onApplyFilters={applyFilters}
          currentFilters={filters}
        />
      )}
    </>
  );
};

export default SearchForm;